<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- 遮罩层 -->
      <div class="fixed inset-0 bg-black/50 transition-opacity" @click="handleClose"></div>

      <!-- 弹窗主体 -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-lg bg-white rounded-2xl shadow-xl transform transition-all">
          <!-- 头部 -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h3 class="text-lg font-semibold text-slate-900">
              {{ referrerData?.id ? '编辑引入人' : '新增引入人' }}
            </h3>
            <button
              @click="handleClose"
              class="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 表单内容 -->
          <div class="p-6">
            <el-form
              ref="formRef"
              :model="formData"
              :rules="rules"
              label-width="100px"
              class="space-y-4"
            >
              <el-form-item label="姓名" prop="name">
                <input
                  v-model="formData.name"
                  type="text"
                  placeholder="请输入引入人姓名"
                  class="form-input w-full"
                >
              </el-form-item>

              <el-form-item label="联系电话" prop="phone">
                <input
                  v-model="formData.phone"
                  type="text"
                  placeholder="请输入联系电话"
                  class="form-input w-full"
                >
              </el-form-item>

              <el-form-item label="微信号" prop="wechat">
                <input
                  v-model="formData.wechat"
                  type="text"
                  placeholder="请输入微信号"
                  class="form-input w-full"
                >
              </el-form-item>

              <el-form-item label="引入类型" prop="referrerType">
                <select
                  v-model="formData.referrerType"
                  class="form-select-native w-full"
                >
                  <option value="">请选择引入类型</option>
                  <option value="0">渠道</option>
                  <option value="1">客户转介绍</option>
                  <option value="2">政府</option>
                  <option value="3">其他</option>
                </select>
              </el-form-item>

              <el-form-item label="备注">
                <textarea
                  v-model="formData.remark"
                  rows="3"
                  placeholder="请输入备注（选填）"
                  class="form-textarea"
                ></textarea>
              </el-form-item>
            </el-form>
          </div>

          <!-- 底部按钮 -->
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            <button
              @click="handleClose"
              class="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 transition-colors"
            >
              取消
            </button>
            <button
              @click="handleSubmit"
              :disabled="submitting"
              class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              <span v-if="submitting">保存中...</span>
              <span v-else>保存</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { createReferrer, updateReferrer } from '@/api/referrer'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  referrerData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const formRef = ref(null)
const submitting = ref(false)

// 表单数据
const formData = reactive({
  name: '',
  phone: '',
  wechat: '',
  referrerType: '',
  remark: ''
})

// 自定义验证器：手机号格式
const validatePhone = (rule, value, callback) => {
  if (value && !/^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$/.test(value)) {
    callback(new Error('请输入正确的手机号码或电话号码'))
  } else {
    callback()
  }
}

// 表单验证规则
const rules = {
  name: [
    { required: true, message: '请输入引入人姓名', trigger: 'blur' },
    { min: 1, max: 50, message: '姓名长度不能超过50个字符', trigger: 'blur' }
  ],
  phone: [
    { validator: validatePhone, trigger: 'blur' }
  ],
  wechat: [
    { required: true, message: '请输入微信号', trigger: 'blur' }
  ],
  referrerType: [
    { required: true, message: '请选择引入类型', trigger: 'change' }
  ]
}

// 监听弹窗显示
watch(() => props.visible, (val) => {
  if (val && props.referrerData) {
    // 编辑模式
    formData.id = props.referrerData.id
    formData.name = props.referrerData.name || ''
    formData.phone = props.referrerData.phone || ''
    formData.wechat = props.referrerData.wechat || ''
    formData.referrerType = props.referrerData.referrerType || ''
    formData.remark = props.referrerData.remark || ''
  } else if (val) {
    // 新增模式
    formData.id = null
    formData.name = ''
    formData.phone = ''
    formData.wechat = ''
    formData.referrerType = ''
    formData.remark = ''
  }
})

// 关闭弹窗
const handleClose = () => {
  emit('update:visible', false)
}

// 提交表单
const handleSubmit = async () => {
  try {
    await formRef.value.validate()
    submitting.value = true

    if (formData.id) {
      // 编辑
      await updateReferrer({
        id: formData.id,
        name: formData.name,
        phone: formData.phone,
        wechat: formData.wechat,
        referrerType: formData.referrerType,
        remark: formData.remark
      })
      ElMessage.success('更新成功')
    } else {
      // 新增
      await createReferrer({
        name: formData.name,
        phone: formData.phone,
        wechat: formData.wechat,
        referrerType: formData.referrerType,
        remark: formData.remark
      })
      ElMessage.success('创建成功')
    }

    emit('success')
    handleClose()
  } catch (error) {
    console.error('保存引入人失败:', error)
    if (error !== false) {
      ElMessage.error(error.message || '保存失败')
    }
  } finally {
    submitting.value = false
  }
}
</script>
